import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ProductShowcase.scss";
import useModalA11y from "../../hooks/useModalA11y";

import blackClosed from "../../assets/black-device-gallery.jpg";
import blackOpen from "../../assets/black-device-open.jpg";
import blackOpen1 from "../../assets/black-device-open1.jpg";
import whiteClosed from "../../assets/white-device-gallery.jpg";
import whiteOpen from "../../assets/white-device-open.jpg";
import whiteOpen1 from "../../assets/white-device-open1.jpg";
import blackBgc from "../../assets/device-black-bgc.jpg";
import whiteBgc from "../../assets/device-white-bgc.jpg";
import InstructionEN from "../../assets/galleryInstruction-en.jpg";
import InstructionUA from "../../assets/galleryInstruction-ua.jpg";

export default function ProductShowcase() {
	const { t, i18n } = useTranslation();

	const images = [
		{ src: blackBgc, alt: "Black Revibrain device on display background" },
		{ src: whiteClosed, alt: "White Revibrain device, closed" },
		{ src: whiteOpen, alt: "White Revibrain device, open" },
		{ src: whiteOpen1, alt: "White Revibrain device, open, alternate angle" },
		{ src: blackClosed, alt: "Black Revibrain device, closed" },
		{ src: whiteBgc, alt: "White Revibrain device on display background" },
		{ src: blackOpen, alt: "Black Revibrain device, open" },
		{ src: blackOpen1, alt: "Black Revibrain device, open, alternate angle" },
	];

	const [activeIndex, setActiveIndex] = useState(null);
	const [showInstructions, setShowInstructions] = useState(false);

	const closeLightbox = useCallback(() => setActiveIndex(null), []);
	const closeInstructions = useCallback(() => setShowInstructions(false), []);
	const lightboxRef = useModalA11y(activeIndex !== null, closeLightbox);
	const instructionsRef = useModalA11y(showInstructions, closeInstructions);

	const instructionImage =
		i18n.language === "ua" ? InstructionUA : InstructionEN;

	const next = () => setActiveIndex((prev) => (prev + 1) % images.length);

	const prev = () =>
		setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

	return (
		<section id="showcase" className="section dark">
			<div className="container">
				<h2>{t("showcase.title")}</h2>
				<p>{t("showcase.intro")}</p>

				{/* GALLERY */}
				<div className="showcase__grid">
					{images.map((image, idx) => (
						<button
							key={idx}
							type="button"
							className="showcase__item"
							onClick={() => setActiveIndex(idx)}>
							<img src={image.src} alt={image.alt} />
						</button>
					))}
				</div>

				{/* BUTTON */}
				<button
					className="showcase__btn"
					onClick={() => setShowInstructions(true)}>
					{t("showcase.instructionsBtn")}
				</button>

				{/* IMAGE MODAL */}
				{activeIndex !== null && (
					<div className="lightbox" onClick={closeLightbox}>
						<div
							ref={lightboxRef}
							className="lightbox__content"
							role="dialog"
							aria-modal="true"
							aria-label={images[activeIndex].alt}
							tabIndex={-1}
							onClick={(e) => e.stopPropagation()}>
							<button
								className="lightbox__close"
								aria-label="Close"
								onClick={closeLightbox}>
								×
							</button>

							<button
								className="lightbox__prev"
								aria-label="Previous image"
								onClick={(e) => {
									e.stopPropagation();
									prev();
								}}>
								‹
							</button>

							<img
								src={images[activeIndex].src}
								alt={images[activeIndex].alt}
								className="lightbox__img"
							/>

							<button
								className="lightbox__next"
								aria-label="Next image"
								onClick={(e) => {
									e.stopPropagation();
									next();
								}}>
								›
							</button>
						</div>
					</div>
				)}

				{/* INSTRUCTIONS MODAL */}
				{showInstructions && (
					<div className="lightbox" onClick={closeInstructions}>
						<div
							ref={instructionsRef}
							className="lightbox__content"
							role="dialog"
							aria-modal="true"
							aria-label={t("showcase.instructionsBtn")}
							tabIndex={-1}
							onClick={(e) => e.stopPropagation()}>
							<button
								className="lightbox__close"
								aria-label="Close"
								onClick={closeInstructions}>
								×
							</button>

							<img
								src={instructionImage}
								alt={`Product usage instructions (${
									i18n.language === "ua" ? "Ukrainian" : "English"
								})`}
								className="lightbox__img"
							/>
						</div>
					</div>
				)}
			</div>
		</section>
	);
}
